import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Alert, Button, Col, Nav, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../shared/permissions';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useConfirm } from '../../shared/ConfirmContext';
import VehicleTariffManagement from './VehicleTariffManagement';
import { useVehicle, useDeleteVehicle } from '../../entities/vehicle';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import StatusBadge from '../../components/ui/StatusBadge';
import StatsCard from '../../components/ui/StatsCard';
import EmptyState from '../../components/feedback/EmptyState';

const TransportDetail = () => {
  const { transportId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();

  const [allProjects, setAllProjects] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const { data: vehicle, isLoading: loading } = useVehicle(transportId);
  const projects = vehicle?.active_projects ?? [];
  const statistics = vehicle ? {
    ...vehicle.project_statistics,
    category: vehicle.category_name || '-',
    status: vehicle.available ? t('transport.status.available') : t('transport.status.unavailable'),
    technical_check_status: vehicle.technical_check_status || 'unknown',
  } : null;

  const fetchAllProjects = async () => {
    try {
      const response = await api.get(`projects/api/projects/?vehicle=${transportId}`);
      const projectsData = response.data || response || [];
      setAllProjects(projectsData);
    } catch (err) {
      setError(t('transport.errors.failedToLoadProjects'));
    }
  };

  const deleteVehicle = useDeleteVehicle();
  const deleteMutation = {
    isPending: deleteVehicle.isPending,
    mutate: () => deleteVehicle.mutate(transportId, {
      onSuccess: () => navigate('/transport'),
      onError: (err) => setError(err.message || t('transport.errors.failedToDelete')),
    }),
  };

  const handleDelete = async () => {
    if (await confirm(t('transport.confirmDelete'))) {
      deleteMutation.mutate();
    }
  };

  const getTechnicalCheckStatus = () => {
    if (!vehicle) return null;

    const status = vehicle.technical_check_status;
    const days = vehicle.days_until_technical_check;

    let color = '#6c757d';
    let text = t('transport.technicalCheck.noDate');

    if (status === 'overdue') {
      color = '#d63939';
      text = `${t('transport.technicalCheck.overdue')} (${Math.abs(days)} ${t('transport.daysOverdue')})`;
    } else if (status === 'due_soon') {
      color = '#f76707';
      text = `${t('transport.technicalCheck.dueSoon')} (${days} ${t('transport.days')})`;
    } else if (status === 'ok') {
      color = '#2fb344';
      text = `${t('transport.technicalCheck.ok')} (${days} ${t('transport.days')})`;
    }

    return { color, text };
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.notSet');
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusText = (status) => {
    if (typeof status === 'object' && status?.name) {
      return status.name;
    }

    switch (status) {
      case 'active': return t('projects.statuses.active');
      case 'planning': return t('projects.statuses.planning');
      case 'completed': return t('projects.statuses.completed');
      case 'cancelled': return t('projects.statuses.cancelled');
      case 'on_hold': return t('projects.statuses.on_hold');
      default: return status || '';
    }
  };

  const getStatusBadgeColor = (status) => {
    const statusName = typeof status === 'object' && status?.name ? status.name.toLowerCase() : status;

    switch (statusName) {
      case 'active':
      case 'aktivní':
      case 'plánováno':
        return '#206bc4';
      case 'planning':
        return '#4299e1';
      case 'completed':
      case 'dokončeno':
        return '#2fb344';
      case 'cancelled':
      case 'zrušeno':
        return '#d63939';
      case 'on_hold':
      case 'pozastaveno':
        return '#f59f00';
      default: return '#6c7a91';
    }
  };

  if (loading) {
    return (
      <PageShell pretitle={t('transport.title')} title={t('transport.detail', 'Detail vozidla')}>
        <PageSection>
          <CenteredLoader message={t('transport.loading')} />
        </PageSection>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell pretitle={t('transport.title')} title={t('transport.detail', 'Detail vozidla')}>
        <PageSection>
          <Alert variant="danger" className="mb-0">{error}</Alert>
        </PageSection>
      </PageShell>
    );
  }

  if (!vehicle) {
    return (
      <PageShell pretitle={t('transport.title')} title={t('transport.detail', 'Detail vozidla')}>
        <PageSection>
          <EmptyState
            title={t('transport.vehicleNotFound')}
            action={<Button as={Link} to="/transport">{t('common.back')}</Button>}
          />
        </PageSection>
      </PageShell>
    );
  }

  const technicalCheckStatus = getTechnicalCheckStatus();

  return (
    <PageShell
      pretitle={t('transport.title')}
      title={vehicle.name}
      description={(
        <>
          {vehicle.category_name && (
            <StatusBadge className="me-2" color={vehicle.category_color || '#6c757d'} label={vehicle.category_name} />
          )}
          <StatusBadge color={vehicle.available ? '#2fb344' : '#d63939'} label={vehicle.available ? t('transport.status.available') : t('transport.status.unavailable')} />
        </>
      )}
      actions={(
        <>
          <Button as={Link} to="/transport" variant="outline-secondary">
            <i className="ti ti-arrow-left me-2" aria-hidden="true" />
            {t('common.back')}
          </Button>
          {canManage && (
            <Button as={Link} to={`/transport/${transportId}/edit`}>
              <i className="ti ti-edit me-2" aria-hidden="true" />
              {t('common.edit')}
            </Button>
          )}
          {canManage && (
            <Button variant="outline-danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
              <i className="ti ti-trash me-2" aria-hidden="true" />
              {t('common.delete')}
            </Button>
          )}
        </>
      )}
    >
      <div className="app-stat-grid mb-4">
        <StatsCard title={t('transport.totalProjects')} value={statistics?.total_projects || 0} icon="briefcase" colorClass="primary" />
        <StatsCard title={t('transport.activeProjects')} value={statistics?.active_projects || 0} icon="bolt" colorClass="success" />
        <StatsCard title={t('transport.fields.category')} value={statistics?.category || '-'} icon="truck" colorClass="azure" />
        <StatsCard title={t('transport.technicalCheck.status')} value={technicalCheckStatus?.text || t('transport.technicalCheck.noDate')} icon="shield-check" colorClass="orange" />
      </div>

      <PageSection bodyClassName="pt-3">
        <Nav className="app-tabnav" activeKey={activeTab}>
          <Nav.Item>
            <Nav.Link as="button" eventKey="overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
              <i className="ti ti-bookmark me-2" aria-hidden="true" />
              {t('transport.overview')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as="button" eventKey="projects" active={activeTab === 'projects'} onClick={() => {
              setActiveTab('projects');
              if (allProjects.length === 0) {
                fetchAllProjects();
              }
            }}>
              <i className="ti ti-briefcase me-2" aria-hidden="true" />
              {t('projects.title')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as="button" eventKey="maintenance" active={activeTab === 'maintenance'} onClick={() => setActiveTab('maintenance')}>
              <i className="ti ti-settings me-2" aria-hidden="true" />
              {t('transport.maintenance')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as="button" eventKey="tariffs" active={activeTab === 'tariffs'} onClick={() => setActiveTab('tariffs')}>
              <i className="ti ti-receipt me-2" aria-hidden="true" />
              {t('transport.tariffs.title')}
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <div className="pt-4">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <Row className="g-4">
                  <Col md={6}>
                    <PageSection title={t('transport.vehicleInformation')} description={t('transport.detailPage.vehicleInfoDescription')}>
                        <div className="datagrid">
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('transport.fields.name')}</div>
                            <div className="datagrid-content">{vehicle.name}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('transport.fields.licencePlate')}</div>
                            <div className="datagrid-content">{vehicle.licence_plate || t('common.notSet')}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('transport.fields.category')}</div>
                            <div className="datagrid-content">
                              {vehicle.category_name ? (
                                <StatusBadge color={vehicle.category_color || '#6c757d'} label={vehicle.category_name} />
                              ) : (
                                <span className="text-muted">{t('common.notSet')}</span>
                              )}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('transport.fields.status')}</div>
                            <div className="datagrid-content">
                              {vehicle.available ? (
                                <StatusBadge color="#2fb344" label={t('transport.status.available')} />
                              ) : (
                                <StatusBadge color="#d63939" label={t('transport.status.unavailable')} />
                              )}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('transport.fields.description')}</div>
                            <div className="datagrid-content" style={{ whiteSpace: 'pre-wrap' }}>
                              {vehicle.description || t('common.notSet')}
                            </div>
                          </div>
                        </div>
                    </PageSection>
                  </Col>

                  <Col md={6}>
                    <PageSection title={t('transport.technicalCheck.title')} description={t('transport.detailPage.technicalCheckDescription')}>
                        <div className="datagrid">
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('transport.fields.nextTechnicalCheck')}</div>
                            <div className="datagrid-content">
                              <strong>{formatDate(vehicle.next_technical_check_date)}</strong>
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('transport.technicalCheck.status')}</div>
                            <div className="datagrid-content">
                              <StatusBadge color={technicalCheckStatus.color} label={technicalCheckStatus.text} />
                            </div>
                          </div>
                          {vehicle.next_technical_check_date && (
                            <div className="datagrid-item">
                              <div className="datagrid-title">{t('transport.technicalCheck.daysUntil')}</div>
                              <div className="datagrid-content">
                                <strong>
                                  {vehicle.days_until_technical_check !== null
                                    ? `${vehicle.days_until_technical_check} ${t('transport.days')}`
                                    : t('common.notCalculated')
                                  }
                                </strong>
                              </div>
                            </div>
                          )}
                        </div>
                    </PageSection>

                    {/* Warning Cards */}
                    {vehicle.technical_check_status === 'overdue' && (
                      <Alert variant="danger" className="mt-3 mb-0">
                        <div className="fw-semibold mb-1">{t('transport.technicalCheck.overdueWarning')}</div>
                        <div>{t('transport.technicalCheck.overdueMessage')}</div>
                      </Alert>
                    )}

                    {vehicle.technical_check_status === 'due_soon' && (
                      <Alert variant="warning" className="mt-3 mb-0">
                        <div className="fw-semibold mb-1">{t('transport.technicalCheck.dueSoonWarning')}</div>
                        <div>{t('transport.technicalCheck.dueSoonMessage')}</div>
                      </Alert>
                    )}
                  </Col>
                </Row>
              )}

              {/* Projects Tab */}
              {activeTab === 'projects' && (
                <PageSection title={t('projects.title')} description={t('transport.detailPage.projectAssignmentsDescription')}>
                  {allProjects.length === 0 ? (
                    <EmptyState title={t('transport.noProjects')} message={t('transport.noProjectsMessage')} />
                  ) : (
                    <Row className="g-3">
                      {allProjects.map((project) => (
                        <Col key={project.id} md={6} lg={4}>
                          <div className="card">
                            <div className="card-body">
                              <h3 className="card-title">
                                <Link to={`/projects/${project.id}`} className="text-reset">
                                  {project.name}
                                </Link>
                              </h3>
                              <p className="text-muted">{project.description || t('common.noDescription')}</p>
                              <div className="d-flex align-items-center justify-content-between">
                                <StatusBadge color={getStatusBadgeColor(project.status)} label={getStatusText(project.status)} />
                                <small className="text-muted">
                                  {formatDate(project.start_date)}
                                </small>
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  )}
                </PageSection>
              )}

              {/* Maintenance Tab */}
              {activeTab === 'maintenance' && (                <PageSection
                  title={t('transport.technicalCheck.schedule')}
                  description={t('transport.detailPage.maintenanceDescription')}
                >
                          <div className="datagrid">
                            <div className="datagrid-item">
                              <div className="datagrid-title">{t('transport.fields.nextTechnicalCheck')}</div>
                              <div className="datagrid-content">
                                <strong>{formatDate(vehicle.next_technical_check_date)}</strong>
                              </div>
                            </div>
                            <div className="datagrid-item">
                              <div className="datagrid-title">{t('transport.technicalCheck.status')}</div>
                              <div className="datagrid-content">
                                <StatusBadge color={technicalCheckStatus.color} label={technicalCheckStatus.text} />
                              </div>
                            </div>
                            {vehicle.days_until_technical_check !== null && (
                              <div className="datagrid-item">
                                <div className="datagrid-title">{t('transport.technicalCheck.daysUntil')}</div>
                                <div className="datagrid-content">
                                  <strong>{vehicle.days_until_technical_check} {t('transport.days')}</strong>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="mt-4">
                            <Button as={Link} to={`/transport/${transportId}/edit`}>
                              <i className="ti ti-calendar-event me-2" aria-hidden="true" />
                              {t('transport.updateTechnicalCheck')}
                            </Button>
                          </div>
                </PageSection>
              )}

              {/* Tariffs Tab */}
              {activeTab === 'tariffs' && (
                <VehicleTariffManagement vehicleId={transportId} />
              )}
        </div>
      </PageSection>
    </PageShell>
  );
};

export default TransportDetail;
